import { useCallback, useMemo, useState } from 'react';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import history from '../../history';
import { removeView } from '../../services/viewServices';
import Avatar from '../avatar/Avatar';
import { Dropdown, DropdownItem } from '../Dropdown';
import DeleteModal from '../models/deleteModel';
import ModalCustom from '../models/modal';
import { BlankDiv, Icon, Table, UserName, ViewItem, AvatarsGroup, AvatarWrapper, PlusAvatar } from './style';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { useDispatch } from 'react-redux';
import { RawView } from '../../mobx/interfaces/view';
import * as LucideIcons from 'lucide-react';
import { ViewModel } from '../../mobx/models/view';
import ViewIcon from '../ViewIcon';
import { ICON_COLORS, ICON_TYPE } from '../../global/constants';

const ViewsTable = () => {
  //state variables
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  //Mobx stores variables
  const { viewStore, workspaceStore } = rootStore;
  const { views, loading } = viewStore;
  const { currentWorkspace } = workspaceStore;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  //other variables
  const dispatch = useDispatch();

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedViewId) return;
    const result = await dispatch(removeView(selectedViewId));
    if (result) {
      rootStore.viewStore.setViews(views.filter((view) => view.id !== selectedViewId) as RawView[]);
      setShowDeleteModal(false);
      setSelectedViewId(null);
    }
  }, [dispatch, selectedViewId, views]);

  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false);
  }, [setShowDeleteModal]);

  const onEditClick = useCallback(
    (viewId: string) => {
      history.push(`/workspace/${currentWorkspace?.id}/view/${viewId}?isEdit=true`);
    },
    [currentWorkspace?.id]
  );

  const onShareClick = (e?: React.MouseEvent<Element, MouseEvent>) => {
    if (e) {
      e.stopPropagation();
    }
  };

  const onDeleteClick = useCallback(
    async (viewId: string) => {
      setSelectedViewId(viewId);
      setShowDeleteModal(true);
    },
    [setSelectedViewId, setShowDeleteModal]
  );

  const dropdownItems = [
    { label: 'Edit', iconName: 'edit-icon', onClick: onEditClick },
    { label: 'Share', iconName: 'share-icon', onClick: onShareClick },
    { label: 'Delete', iconName: 'more-menu-delete-icon', onClick: onDeleteClick }
  ];

  const onClickDropdownItem = (label: string, viewId: string) => {
    if (label === 'Delete') {
      setShowDeleteModal(true);
      onDeleteClick(viewId);
    } else if (label === 'Edit') {
      onEditClick(viewId);
    } else if (label === 'Share') {
      onShareClick();
    }
  };
  // Get all Lucide icons
  const allIcons = useMemo(() => {
    return Object.entries(LucideIcons)
      .filter(([name]) => /^[A-Z].*Icon$/.test(name))
      .map(([name, component]) => ({
        name,
        displayName: name
          .replace(/([A-Z])/g, ' $1')
          .replace(/^ /, '')
          .replace(/ Icon$/, ''),
        component: component as React.ComponentType<{ size?: number }>
      }));
  }, []);

  const getSelectedIconComponent = useCallback(
    (view: ViewModel) => {
      const defaultIcon = allIcons[0];
      if (!view?.icon?.image) return defaultIcon.component;
      const found = allIcons.find((icon) => icon.name === view.icon.image);
      return found ? found.component : defaultIcon.component;
    },
    [allIcons]
  );

  return (
    <>
      <Table>
        {/* Table Header */}
        <ViewItem>
          <UserName>
            <p>Name</p>
          </UserName>
          <UserName>
            <p>Access</p>
          </UserName>
          <BlankDiv />
        </ViewItem>

        {/* Table Rows */}
        {views.map((view, idx) => {
          const IconComponent = getSelectedIconComponent(view);
          return (
            <ViewItem key={idx} onClick={() => history.push(`/workspace/${currentWorkspace?.id}/view/${view.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 2 }}>
                <Icon>
                  {view.icon.type === ICON_TYPE.ICON ? (
                    <ViewIcon backgroundColor={ICON_COLORS[view.color]}>
                      <IconComponent size={16} />
                    </ViewIcon>
                  ) : (
                    <span style={{ fontSize: 16 }}>{view.icon.image}</span>
                  )}
                </Icon>
                <UserName style={{ flex: 2 }}>
                  <p>{view.name}</p>
                  <span>{view.description}</span>
                </UserName>
              </div>
              <UserName style={{ flex: 2 }}>
                {/* Avatars */}
                <AvatarsGroup>
                  {view.users.slice(0, 4).map((user, i) => (
                    <AvatarWrapper key={i}>
                      <Avatar name={user.name} size={24} imgSrc={user.profile_photo || ''} />
                    </AvatarWrapper>
                  ))}
                  {view.users.length > 4 && <PlusAvatar>+{view.users.length - 4}</PlusAvatar>}
                </AvatarsGroup>
              </UserName>
              {/* Menu Icon */}
              <Dropdown
                content={
                  <SVGIcon name='task-detail-dot-icon' width='16' height='16' viewBox='0 0 16 16' className='icon' />
                }
                trigger='click'>
                {dropdownItems.map((item) => (
                  <DropdownItem
                    key={item.label}
                    label={item.label}
                    iconName={item.iconName}
                    iconSize={16}
                    tone={item.label === 'Delete' ? 'critical' : 'primary'}
                    iconViewBox={item.label === 'Delete' ? '0 0 18 18' : '0 0 16 16'}
                    onClick={() => onClickDropdownItem(item.label, view.id)}
                  />
                ))}
              </Dropdown>
            </ViewItem>
          );
        })}
      </Table>
      <ModalCustom open={showDeleteModal} onClose={handleCloseModal} width={334}>
        <DeleteModal
          modalTitle='Delete View?'
          description='Are you sure you want to delete this view?'
          onClose={handleCloseModal}
          onOk={handleDeleteConfirm}
          buttonText='Delete'
          isPrimaryButton={false}
          isYes={true}
          loading={loading}
        />
      </ModalCustom>
    </>
  );
};
export default observer(ViewsTable);
